import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { SearchHit } from '../types';
import { useChat } from '../store';
import { relativeTime } from '../utils/time';
import { Avatar } from './Avatar';

interface SearchModalProps {
  onClose: () => void;
}

export function SearchModal({ onClose }: SearchModalProps) {
  const users = useChat((s) => s.users);
  const conversations = useChat((s) => s.conversations);
  const selectConversation = useChat((s) => s.selectConversation);
  const setJumpToMessage = useChat((s) => s.setJumpToMessage);

  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);

  // Esc to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    inputRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!q.trim()) {
      setHits(null);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.search(q.trim());
        setHits(res.hits);
      } catch {
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, [q]);

  const usersById = useMemo(() => {
    const m = new Map<string, (typeof users)[number]>();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  const convsById = useMemo(() => {
    const m = new Map<string, (typeof conversations)[number]>();
    for (const c of conversations) m.set(c.id, c);
    return m;
  }, [conversations]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center pt-24 px-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-zinc-950 border border-zinc-800 rounded-lg shadow-xl flex flex-col max-h-[70vh]"
      >
        <div className="px-4 py-3 border-b border-zinc-900">
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search messages…"
            className="w-full bg-transparent border-none outline-none text-sm placeholder:text-zinc-500"
          />
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {loading && (
            <p className="text-center text-xs text-zinc-500 py-4">Searching…</p>
          )}
          {!loading && hits === null && (
            <p className="text-center text-xs text-zinc-500 py-8">
              Type to search across all your conversations.
            </p>
          )}
          {!loading && hits !== null && hits.length === 0 && (
            <p className="text-center text-xs text-zinc-500 py-8">No matches.</p>
          )}
          {!loading &&
            hits !== null &&
            hits.map((hit) => {
              const sender = usersById.get(hit.senderId);
              const conv = convsById.get(hit.conversationId);
              const convLabel =
                conv?.type === 'dm'
                  ? (() => {
                      const other = conv.participants.find((p) => p.id !== sender?.id);
                      return other?.displayName ?? other?.email ?? 'DM';
                    })()
                  : conv?.name ?? 'Group';
              return (
                <button
                  key={hit.messageId}
                  onClick={() => {
                    selectConversation(hit.conversationId);
                    // Set the jump request — MessageList consumes it on
                    // its next render. Works immediately if the message
                    // is already in the loaded slice; for unloaded
                    // history the request is dropped (a future "messages
                    // around id" endpoint can promote this to always-
                    // works). Either way, the conversation is selected.
                    setJumpToMessage(hit.messageId);
                    onClose();
                  }}
                  className="w-full text-left px-4 py-2 flex items-start gap-3 hover:bg-zinc-900 transition"
                >
                  <Avatar
                    id={sender?.id ?? hit.senderId}
                    displayName={sender?.displayName ?? null}
                    email={sender?.email ?? '?'}
                    size={28}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium truncate">
                        {sender?.displayName ?? sender?.email ?? 'Unknown'}
                      </span>
                      <span className="text-[10px] text-zinc-500 truncate">
                        in {convLabel} · {relativeTime(hit.createdAt)}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-300 truncate mt-0.5">
                      {snippet(hit.body, q.trim(), 120)}
                    </p>
                  </div>
                </button>
              );
            })}
        </div>

        <div className="px-4 py-2 border-t border-zinc-900 text-[10px] text-zinc-500">
          Esc to close · Click a result to jump to that conversation
        </div>
      </div>
    </div>
  );
}

// Take a substring of `text` around the first match of `query`, padded by ~20
// chars on each side. Highlights via simple wrapping.
function snippet(text: string, query: string, maxLen: number): string {
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const idx = q ? lower.indexOf(q) : -1;
  if (idx === -1) {
    return text.length > maxLen ? text.slice(0, maxLen) + '…' : text;
  }
  const start = Math.max(0, idx - 30);
  const end = Math.min(text.length, idx + q.length + 60);
  let out = text.slice(start, end);
  if (start > 0) out = '…' + out;
  if (end < text.length) out = out + '…';
  return out;
}
